import React, { useState, useCallback, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2, ArrowRight, ArrowLeft, Check } from "lucide-react";
import { API, axios } from "@/utils/constants";
import {
  createDefaultAfsolForm,
  applyVehicleLookupToForm,
  applyLoanToAfsolForm,
  personLookupToPartyFields,
} from "@/utils/afsol";
import { STEPS, lookupPerson } from "./wizard/constants";
import WizardStepIndicator from "./wizard/WizardStepIndicator";
import WizardStepType from "./wizard/WizardStepType";
import WizardStepVehicle from "./wizard/WizardStepVehicle";
import WizardStepParties from "./wizard/WizardStepParties";
import WizardStepCondition from "./wizard/WizardStepCondition";
import WizardStepFinance from "./wizard/WizardStepFinance";

export const NewAfsolWizard = ({ open, onOpenChange, onCreated, createdBy, loanData, editAfsalId }) => {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(() => {
    const base = createDefaultAfsolForm(createdBy);
    return loanData ? applyLoanToAfsolForm(base, loanData) : base;
  });
  const [dealType, setDealType] = useState("");
  const [saving, setSaving] = useState(false);

  const [vLookupLoading, setVLookupLoading] = useState(false);
  const [vehicleFound, setVehicleFound] = useState(false);
  const [matchedLoan, setMatchedLoan] = useState("");

  const [loadingMap, setLoadingMap] = useState({});

  const [sellerHasCoOwner, setSellerHasCoOwner] = useState(false);
  const [buyerHasCoOwner, setBuyerHasCoOwner] = useState(false);
  const [sellerExtraType, setSellerExtraType] = useState("");
  const [buyerExtraType, setBuyerExtraType] = useState("");

  const reset = useCallback(() => {
    setStep(0);
    const base = createDefaultAfsolForm(createdBy);
    setForm(loanData ? applyLoanToAfsolForm(base, loanData) : base);
    setDealType("");
    setVehicleFound(false);
    setMatchedLoan("");
    setSellerHasCoOwner(false);
    setBuyerHasCoOwner(false);
    setSellerExtraType("");
    setBuyerExtraType("");
  }, [createdBy, loanData]);

  // Load existing afsal for editing
  useEffect(() => {
    if (open && editAfsalId) {
      (async () => {
        try {
          const res = await axios.get(`${API}/afsals/${editAfsalId}`);
          const a = res.data;
          const fd = a.form_data || {};
          setForm({ ...createDefaultAfsolForm(createdBy), ...fd });
          setDealType(fd.dealType || "");
          setSellerExtraType(fd.sellerExtraType || "");
          setBuyerExtraType(fd.buyerExtraType || "");
          setSellerHasCoOwner(!!fd.sellerExtraType);
          setBuyerHasCoOwner(!!fd.buyerExtraType);
          setStep(0);
        } catch { toast.error("Villa við að sækja afsal"); }
      })();
    }
  }, [open, editAfsalId, createdBy]);

  // Apply loan data when wizard opens with new loanData
  useEffect(() => {
    if (open && loanData) {
      const base = createDefaultAfsolForm(createdBy);
      const updated = applyLoanToAfsolForm(base, loanData);
      setForm(updated);
      const regNum = (loanData.registration_number || "").trim().toUpperCase();
      if (regNum && regNum.length >= 3) {
        setVLookupLoading(true);
        axios.post(`${API}/vehicle-lookup`, { fastanumer: regNum })
          .then((res) => {
            setForm((f) => applyVehicleLookupToForm({ ...f, vehicle: { ...f.vehicle, fastanumer: regNum, registrationNumber: regNum } }, res.data, dealType));
            setVehicleFound(true);
          })
          .catch(() => {})
          .finally(() => setVLookupLoading(false));
      }
    }
  }, [open, loanData, createdBy]);

  const updateField = (section, field, value) => {
    setForm((f) => ({ ...f, [section]: { ...f[section], [field]: value } }));
  };

  const updateParty = (group, key, field, value) => {
    setForm((f) => ({
      ...f,
      [group]: { ...f[group], [key]: { ...f[group][key], [field]: value } },
    }));
  };

  // Vehicle lookup
  const handleVehicleLookup = async () => {
    const fn = (form.vehicle.fastanumer || "").trim().toUpperCase();
    if (!fn || fn.length < 3) { toast.error("Sláðu inn gilt bílnúmer"); return; }
    setVLookupLoading(true);
    setVehicleFound(false);
    setMatchedLoan("");
    try {
      const [vRes, lRes] = await Promise.allSettled([
        axios.post(`${API}/vehicle-lookup`, { fastanumer: fn }),
        axios.get(`${API}/afsals/loan-options`, { params: { query: fn, limit: 5 } }),
      ]);
      if (vRes.status === "fulfilled") {
        setForm((f) => applyVehicleLookupToForm({ ...f, vehicle: { ...f.vehicle, fastanumer: fn, registrationNumber: fn } }, vRes.value.data, dealType));
        setVehicleFound(true);
        toast.success("Ökutæki fundið");
      }
      if (lRes.status === "fulfilled") {
        const exact = (lRes.value.data || []).find((i) => (i.registration_number || "").toUpperCase() === fn);
        if (exact?.loan_id) {
          const loan = await axios.get(`${API}/loans/${exact.loan_id}`);
          setForm((f) => applyLoanToAfsolForm(f, loan.data));
          setMatchedLoan(`${exact.borrower_name} · Lán ${exact.loan_number || "?"}`);
        }
      }
      if (vRes.status === "rejected" && !matchedLoan) {
        toast.error("Ökutæki fannst ekki — skráðu handvirkt");
      }
    } catch { toast.error("Villa í uppflettingu"); }
    finally { setVLookupLoading(false); }
  };

  // Person lookup
  const handlePersonLookup = async (group, key) => {
    const kt = form[group][key].kennitala;
    if (!kt || kt.length !== 10) { toast.error("Sláðu inn 10 stafa kennitölu"); return; }
    const lk = `${group}-${key}`;
    setLoadingMap((m) => ({ ...m, [lk]: true }));
    try {
      const person = await lookupPerson(kt);
      setForm((f) => ({
        ...f,
        [group]: { ...f[group], [key]: personLookupToPartyFields(person, f[group][key]) },
      }));
      toast.success(`${person?.name || "Aðili"} fundinn`);
    } catch { toast.error("Kennitöluuppfletting mistókst"); }
    finally { setLoadingMap((m) => ({ ...m, [lk]: false })); }
  };

  // Save
  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        loan_id: form.general.loanId || undefined,
        loan_number: form.general.loanNumber ? Number(form.general.loanNumber) : undefined,
        registration_number: form.vehicle.fastanumer || form.vehicle.registrationNumber,
        vehicle_type: form.vehicle.displayName || `${form.vehicle.manufacturer} ${form.vehicle.model}`.trim(),
        afsal_date: form.general.afsalDate,
        notes: form.general.comments,
        seller_name: form.seller.owner.name,
        buyer_name: form.buyer.owner.name,
        buyer_kennitala: form.buyer.owner.kennitala,
        form_data: { ...form, dealType, sellerExtraType, buyerExtraType },
      };
      const res = editAfsalId
        ? await axios.put(`${API}/afsals/${editAfsalId}`, payload)
        : await axios.post(`${API}/afsals`, payload);
      toast.success(editAfsalId ? "Afsal uppfært" : "Afsal skráð");
      onCreated?.(res.data);
      onOpenChange(false);
      reset();
    } catch { toast.error("Ekki tókst að vista afsal"); }
    finally { setSaving(false); }
  };

  const canNext = () => {
    if (step === 0) return !!dealType;
    if (step === 1) return !!(form.vehicle.fastanumer || "").trim();
    if (step === 2) return !!(form.buyer.owner.kennitala || form.seller.owner.name);
    return true;
  };

  const stepRenderers = [
    <WizardStepType key="type" dealType={dealType} setDealType={setDealType} setForm={setForm} />,
    <WizardStepVehicle key="vehicle" form={form} updateField={updateField} vehicleFound={vehicleFound} matchedLoan={matchedLoan} vLookupLoading={vLookupLoading} onVehicleLookup={handleVehicleLookup} />,
    <WizardStepParties key="parties"
      form={form} updateField={updateField} updateParty={updateParty}
      loadingMap={loadingMap} onPersonLookup={handlePersonLookup}
      sellerHasCoOwner={sellerHasCoOwner} setSellerHasCoOwner={setSellerHasCoOwner}
      sellerExtraType={sellerExtraType} setSellerExtraType={setSellerExtraType}
      buyerHasCoOwner={buyerHasCoOwner} setBuyerHasCoOwner={setBuyerHasCoOwner}
      buyerExtraType={buyerExtraType} setBuyerExtraType={setBuyerExtraType}
    />,
    <WizardStepCondition key="condition" form={form} updateField={updateField} />,
    <WizardStepFinance key="finance" form={form} setForm={setForm} updateField={updateField} />,
  ];

  const isLast = step === STEPS.length - 1;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="new-afsal-wizard">
        <DialogHeader className="pb-1">
          <DialogTitle className="text-base font-bold">{editAfsalId ? "Breyta afsali" : "Nýtt afsal"}</DialogTitle>
        </DialogHeader>

        <WizardStepIndicator step={step} setStep={setStep} />
        {stepRenderers[step]}

        <div className="flex items-center justify-between pt-3 border-t mt-2">
          <div>
            {step > 0 && (
              <Button variant="ghost" size="sm" onClick={() => setStep((s) => s - 1)} data-testid="wizard-back">
                <ArrowLeft className="h-4 w-4 mr-1" /> Til baka
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => { reset(); onOpenChange(false); }} data-testid="wizard-cancel">
              Hætta við
            </Button>
            {isLast ? (
              <Button size="sm" onClick={handleSave} disabled={saving} className="bg-blue-600 hover:bg-blue-700" data-testid="wizard-save">
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Check className="h-4 w-4 mr-1" />}
                Skrá
              </Button>
            ) : (
              <Button size="sm" onClick={() => setStep((s) => s + 1)} disabled={!canNext()} className="bg-blue-600 hover:bg-blue-700" data-testid="wizard-next">
                Næsta <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
